import React from "react";
import PageFrame from "./PageFrame";

/**
 * Page 03 of the Multi-Family deck - Unit Summary. It takes the place the rent roll has on
 * the multi-tenant deck: an apartment deal has a unit mix, not a tenant roll.
 *
 *   asset overview   x 48..528,  y 131..404   five rows
 *   property photo   x 552..1000, y 131..404
 *   unit mix         x 52..990,  y 424..680   floor plans, then Total / Weighted Avg.
 *
 * The unit-mix rows are the Multi-Family underwriting's floor plans (Current Rent Roll), as
 * read by bov-data-multifamily. The card is sized for the reference's three plans plus head
 * and total at 35px; with more plans the rows share the same height - see mixRowHeight.
 */
// Head, three plans and the total at the template's 35px pitch.
const MIX_AREA_HEIGHT = 5 * 35;
const MIX_MIN_ROW_HEIGHT = 14;

/** Row pitch that keeps `plans` floor plans (plus head and total) inside the card. */
function mixRowHeight(plans) {
    return Math.max(MIX_MIN_ROW_HEIGHT, Math.min(35, Math.floor(MIX_AREA_HEIGHT / (plans + 2))));
}

const MIX_COLUMNS = [
    {key: 'plan', label: 'Floor Plan', align: 'left', width: 196},
    {key: 'units', label: 'Units', align: 'left', width: 122},
    {key: 'avgSf', label: 'Avg SF', align: 'left', width: 100},
    {key: 'rent', label: 'Rent/Mo', align: 'right', width: 110},
    {key: 'rentSf', label: 'Rent/ SF', align: 'right', width: 126},
    {key: 'proformaRent', label: 'Proforma Rent/Mo', align: 'right', width: 128},
    {key: 'proformaRentSf', label: 'Proforma Rent/SF', align: 'right', width: 128},
];

export default function UnitSummary({summary, subProduct, propertyImageUrls, pageNo, totalPages}) {
    const rowHeight = mixRowHeight(summary.unitMix.length);
    return (
        <PageFrame
            eyebrow={`OFFERING SUMMARY  •  ${subProduct}`}
            title='Unit '
            titleAccent='Summary.'
            pageNo={pageNo}
            totalPages={totalPages}
        >
            <div className='bov-units'>
                <div className='bov-units__top'>
                    <div className='bov-card bov-units__overview'>
                        <div className='bov-card__title'>ASSET OVERVIEW</div>
                        {summary.overview.map(row => (
                            <div key={row.label} className='bov-kv'>
                                <div className='bov-kv__label'>{row.label}</div>
                                <div className='bov-kv__value'>{row.value}</div>
                            </div>
                        ))}
                    </div>

                    <div className='bov-photo bov-units__photo'>
                        <PropertyPhoto urls={propertyImageUrls || []}/>
                    </div>
                </div>

                <div className='bov-card bov-units__mix'>
                    <div className='bov-card__title'>UNIT MIX &amp; IN-PLACE RENTS VS. PROFORMA RENTS</div>
                    <div className='bov-mix'>
                        <MixRow head row={null} height={rowHeight}/>
                        {summary.unitMix.map((row, index) => <MixRow key={`${row.plan}-${index}`} row={row} height={rowHeight}/>)}
                        <MixRow row={summary.unitMixTotal} total height={rowHeight}/>
                    </div>
                </div>
            </div>
        </PageFrame>
    );
}

/**
 * The property photo: Website Photo 1, or the first of Photos 2-5 that is filled in. Photo 1
 * is often left empty, and a Multi-Family deck without it printed an empty panel. A photo that
 * fails to load - a stale or mistyped path - also steps on to the next one rather than leaving
 * the panel blank.
 */
class PropertyPhoto extends React.Component {
    constructor(props) {
        super(props);
        this.state = {index: 0};
        this.next = () => this.setState(({index}) => ({index: index + 1}));
    }

    componentWillReceiveProps(nextProps) {
        if ((nextProps.urls || []).join('|') !== (this.props.urls || []).join('|')) {
            this.setState({index: 0});
        }
    }

    render() {
        const url = this.props.urls[this.state.index];
        if (!url) return null;
        return <img key={url} className='bov-photo__img' src={url} alt='' onError={this.next}/>;
    }
}

function MixRow({row, head, total, height}) {
    // Below the template pitch the type shrinks with the row so it does not overlap.
    const style = height < 35 ? {height: `${height}px`, fontSize: `${Math.max(9, Math.round(height * 0.45))}px`} : undefined;
    return (
        <div className={`bov-mix__row${head ? ' bov-mix__row--head' : ''}${total ? ' bov-mix__row--total' : ''}`} style={style}>
            {MIX_COLUMNS.map(column => (
                <div
                    key={column.key}
                    className={`bov-mix__cell bov-mix__cell--${column.align}`}
                    style={{width: `${column.width}px`}}
                >
                    {head ? column.label : row[column.key]}
                </div>
            ))}
        </div>
    );
}
